import { createContext, useContext, useMemo, useReducer, type ReactNode } from "react";
import { createAthleteProfileRepository, type AthleteProfileRepository } from "./athleteProfileRepository";
import { reduceAthleteProfileState } from "./athleteProfileState";
import type { AthleteProfile } from "./types/athleteProfile";

type AthleteProfileContextValue = {
  athleteProfile: AthleteProfile;
  updateAthleteProfile: (nextProfile: AthleteProfile) => AthleteProfile;
  resetAthleteProfile: () => AthleteProfile;
};

const AthleteProfileContext = createContext<AthleteProfileContextValue | null>(null);

type AthleteProfileProviderProps = {
  children: ReactNode;
  repository?: AthleteProfileRepository;
};

export function AthleteProfileProvider({ children, repository: repositoryOverride }: AthleteProfileProviderProps) {
  const repository = useMemo(
    () => repositoryOverride ?? createAthleteProfileRepository(),
    [repositoryOverride]
  );
  const [state, dispatch] = useReducer(reduceAthleteProfileState, undefined, () => ({
    profile: repository.loadProfile(),
    hasUnsavedChanges: false
  }));
  const athleteProfile = state.profile;

  function updateAthleteProfile(nextProfile: AthleteProfile): AthleteProfile {
    const saved = repository.saveProfile(nextProfile);
    dispatch({ type: "saved", profile: saved });
    return saved;
  }

  function resetAthleteProfile(): AthleteProfile {
    const reset = repository.resetProfile();
    dispatch({ type: "reset", profile: reset });
    return reset;
  }

  return (
    <AthleteProfileContext.Provider value={{ athleteProfile, updateAthleteProfile, resetAthleteProfile }}>
      {children}
    </AthleteProfileContext.Provider>
  );
}

export function useAthleteProfile() {
  const context = useContext(AthleteProfileContext);

  if (!context) {
    throw new Error("useAthleteProfile must be used within AthleteProfileProvider.");
  }

  return context;
}
